import { api } from './api-client';
import { STORAGE_KEYS, getItem, removeItem, setItem } from './storage';

export type TenantKey = 'WITTY' | 'PSP' | 'YPC';

export interface AuthUser {
  id: string;
  username: string;
  displayName: string;
  role: string;
  roles: string[];
  permissions: string[];
  tenant: TenantKey;
  /** Linked HR employee record id, if any. Factory workers have one; admins may not. */
  employeeId: string | null;
}

/** Backend /auth/login returns snake_case + nested user. */
interface BackendLoginResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    username: string;
    name?: string;
    roles?: string[];
    permissions?: string[];
    homeCompany?: string;
    employee?: { id: string; code?: string } | null;
  };
}

const TENANT_BY_COMPANY: Record<string, TenantKey> = {
  '1000': 'WITTY',
  '2000': 'PSP',
  '3000': 'YPC',
  WITTY: 'WITTY',
  PSP: 'PSP',
  YPC: 'YPC',
};

const COMPANY_BY_TENANT: Record<TenantKey, string> = {
  WITTY: '1000',
  PSP: '2000',
  YPC: '3000',
};

export async function login(
  username: string,
  password: string,
  tenant: TenantKey = 'PSP',
  rememberMe = false,
): Promise<AuthUser> {
  await removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  await removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  await removeItem(STORAGE_KEYS.USER);
  await removeItem(STORAGE_KEYS.TENANT);

  const companyId = COMPANY_BY_TENANT[tenant];
  const { data } = await api.post<BackendLoginResponse>('/auth/login', {
    username,
    password,
    companyId,
    rememberMe,
  });

  const user: AuthUser = {
    id: data.user.id,
    username: data.user.username,
    displayName: data.user.name ?? data.user.username,
    role: data.user.roles?.[0] ?? 'USER',
    roles: data.user.roles ?? [],
    permissions: data.user.permissions ?? [],
    tenant: TENANT_BY_COMPANY[data.user.homeCompany ?? companyId] ?? tenant,
    employeeId: data.user.employee?.id ?? null,
  };

  await setItem(STORAGE_KEYS.ACCESS_TOKEN, data.access_token);
  await setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refresh_token);
  await setItem(STORAGE_KEYS.USER, JSON.stringify(user));
  await setItem(STORAGE_KEYS.TENANT, user.tenant);
  return user;
}

export async function logout(): Promise<void> {
  const refreshToken = await getItem(STORAGE_KEYS.REFRESH_TOKEN);
  if (refreshToken) {
    try {
      await api.post('/auth/logout', { refresh_token: refreshToken });
    } catch {
      void 0;
    }
  }
  await removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  await removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  await removeItem(STORAGE_KEYS.USER);
  await removeItem(STORAGE_KEYS.TENANT);
}
